import { getQueryFromTokens } from '../filters-compiler/getQueryFromTokens';
import { Token } from '../filters-compiler/types';
import { type FilteredView, Category, CategoryQueryMap } from '../types';
import getFirstEmojiFromString from './getFirstEmojiFromString';
import getWords from './getWords';

export const shortListName = 'Shortlist';
export const shortListQuery = 'tag:shortlist AND (in:inbox OR in:later)';
export const shortListQueryWhenShortlistLocationIsEnabled = 'in:shortlist';
export const shortListTag = 'shortlist';
export const todaysDailyDigestId = 'todays-digest';

export const articlesQueries = ['category:article', 'type:article'];
export const epubsQueries = ['category:epub', 'type:epub'];
export const emailsQueries = ['category:email', 'type:email'];
export const pdfsQueries = ['category:pdf', 'type:pdf'];
export const tweetsQueries = ['category:tweet', 'type:tweet'];
export const videosQueries = ['category:video', 'type:video'];

export const categoryQueryMap: CategoryQueryMap = {
  [Category.Article]: articlesQueries,
  [Category.EPUB]: epubsQueries,
  [Category.Email]: emailsQueries,
  [Category.PDF]: pdfsQueries,
  [Category.Tweet]: tweetsQueries,
  [Category.Video]: videosQueries,
};

export const allDefaultCategoriesQueries = [
  ...articlesQueries,
  ...emailsQueries,
  ...pdfsQueries,
  ...epubsQueries,
  ...tweetsQueries,
  ...videosQueries,
];

export function getCategoriesIconMap<T = JSX.Element>({
  articlesIcon,
  emailsIcon,
  epubsIcon,
  pdfsIcon,
  tweetsIcon,
  videosIcon,
}: { articlesIcon: T; emailsIcon: T; epubsIcon: T; pdfsIcon: T; tweetsIcon: T; videosIcon: T; }): {
  [key: string]: T;
} {
  return allDefaultCategoriesQueries.reduce((acc, query) => {
    if (query.includes('article')) {
      return {
        ...acc,
        [query]: articlesIcon,
      };
    }

    if (query.includes('email')) {
      return {
        ...acc,
        [query]: emailsIcon,
      };
    }

    if (query.includes('pdf')) {
      return {
        ...acc,
        [query]: pdfsIcon,
      };
    }

    if (query.includes('epub')) {
      return {
        ...acc,
        [query]: epubsIcon,
      };
    }

    if (query.includes('tweet')) {
      return {
        ...acc,
        [query]: tweetsIcon,
      };
    }

    if (query.includes('video')) {
      return {
        ...acc,
        [query]: videosIcon,
      };
    }

    return acc;
  }, {});
}

export const getFilterViewAbbreviationNameOrEmoji = (name: string): string => {
  if (name === '') {
    return 'NN';
  }

  const [firstChar, secondChar = ''] = name;

  const emoji = getFirstEmojiFromString(name);

  if (emoji) {
    return emoji;
  }

  const words = getWords(name, 'unknown');
  if (words.length === 1) {
    return `${firstChar.toLocaleUpperCase()}${secondChar}`;
  }

  const [firstCharOfSecondWord] = words[1];
  return `${firstChar.toLocaleUpperCase()}${(firstCharOfSecondWord || '').toLocaleUpperCase()}`;
};

export const getFilterViewAbbreviationName = (name: string): string => {
  if (name === '') {
    return 'NN';
  }

  const [firstChar, secondChar = ''] = name;

  const emoji = getFirstEmojiFromString(name);

  if (emoji) {
    return '';
  }

  const words = getWords(name, 'unknown');

  if (words.length === 1) {
    return `${firstChar.toLocaleUpperCase()}${secondChar}`;
  }

  const [firstCharOfSecondWord] = words[1];
  return `${firstChar.toLocaleUpperCase()}${firstCharOfSecondWord.toLocaleUpperCase()}`;
};

// rssSource:xxxx
export const isFilteringBySingleFeed = ({ filterTokens }: { filterTokens: Token[]; }) => {
  return (
    filterTokens.length === 3 &&
    (filterTokens[0].value.toLocaleLowerCase() === 'rsssource' ||
      filterTokens[0].value.toLocaleLowerCase() === 'rss_source')
  );
};

// feed:true AND rssSource:xxxx
export const isFilteringByFeedTrueAndSingleFeed = ({ filterTokens }: { filterTokens: Token[]; }) => {
  const query = getQueryFromTokens(filterTokens);
  return filterTokens.length === 7 && query.startsWith('feed:true AND rssSource:');
};

// When a user is filtering by a feed, we show a special UI, such as an "Edit feed" button on the header.
// Additionally, if they open a feed from a folder, we prepend 'feed:true AND ' to the query.
// This means that both of these queries are considered as filtering by a single feed:
// 1. rssSource:xxxx
// 2. feed:true AND rssSource:xxxx
export const isFilteringBySingleFeedOrByFeedTrueAndSingleFeed = ({
  filterTokens,
}: { filterTokens?: Token[]; }) => {
  if (!filterTokens) {
    return false;
  }

  return (
    isFilteringBySingleFeed({ filterTokens }) || isFilteringByFeedTrueAndSingleFeed({ filterTokens })
  );
};

export const getFeedIdFromTokens = (filterTokens: Token[] | undefined): string => {
  if (!filterTokens) {
    return '';
  }

  if (isFilteringBySingleFeed({ filterTokens })) {
    return filterTokens[2].value;
  }

  if (isFilteringByFeedTrueAndSingleFeed({ filterTokens })) {
    return filterTokens[6].value;
  }

  return '';
};

export const getIsFilteringByOneTag = (filterTokens: Token[] | undefined) => {
  if (!filterTokens) {
    return false;
  }

  return Boolean(filterTokens.length === 3 && filterTokens[0].value === 'tag');
};

export const getTagNameFromTokens = (filterTokens: Token[] | undefined): string => {
  if (!filterTokens || !getIsFilteringByOneTag(filterTokens)) {
    return '';
  }

  return filterTokens[2].value;
};

export function isAllowedToDeleteFilteredView(id?: FilteredView['id']): boolean {
  return !isDailyDigest(id);
}

export function isAllowedToSortFilteredView(id?: FilteredView['id']): boolean {
  return !isDailyDigest(id);
}

export function isAllowedToEditFilteredView(view?: FilteredView): boolean {
  return !isDailyDigest(view?.id) && !view?.rssFolderId;
}

export function isDailyDigest(id?: FilteredView['id']): boolean {
  if (!id) {
    return false;
  }

  return id === todaysDailyDigestId || id.startsWith('digest-');
}

export function isEmptyRssFolder(view: FilteredView | undefined): boolean {
  return Boolean(view?.rssFolderId && !view.query);
}
